import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiErrorMessage } from '@/components/analysis/reportAnalysisUi';
import {
  formatBid,
  formatCount,
  formatPosition,
  formatSearchVolume,
  formatSov,
  marketplaceLabel,
  sovBarRows,
} from '@/components/shareOfVoice/shareOfVoiceFormat';
import { Card } from '@/components/ui/Card';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import type { SovBarRow } from '@/components/shareOfVoice/shareOfVoiceFormat';

function Bars({ title, rows }: { title: string; rows: SovBarRow[] }) {
  return (
    <Card className="space-y-3 p-4">
      <h2 className="text-body-md font-semibold text-foreground">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-body-sm text-foreground-muted">No brand rows in this snapshot.</p>
      ) : (
        rows.map((row) => (
          <div key={row.label}>
            <div className="mb-1 flex items-center justify-between gap-3 text-body-sm">
              <span className={cn(row.own ? 'font-semibold text-foreground' : 'text-foreground')}>{row.label}</span>
              <span className="tabular-nums text-foreground-muted">{formatSov(row.value)}</span>
            </div>
            <div className="h-2 rounded-full bg-surface-muted">
              <div
                className={cn('h-2 rounded-full', row.own ? 'bg-brand-ink' : 'bg-foreground-subtle')}
                style={{ width: `${Math.min(100, Math.max(0, row.value * 100))}%` }}
              />
            </div>
          </div>
        ))
      )}
    </Card>
  );
}

export function ShareOfVoiceDetailPage() {
  const { profileId: profileIdParam, keywordKey = '' } = useParams<{
    profileId: string;
    keywordKey: string;
  }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { range } = useAppDateRange();
  const startDate = range.current.startDate;
  const endDate = range.current.endDate;
  const listTo = `/accounts/${encodeURIComponent(profileId)}/campaigns/sov`;

  const detailQuery = useQuery({
    queryKey: ['sov-detail', profileId, keywordKey, startDate, endDate],
    queryFn: async () => {
      const res = await adsApi.getShareOfVoiceKeyword(profileId, keywordKey, { startDate, endDate });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load keyword');
      return res.data;
    },
    enabled: Boolean(profileId && keywordKey),
  });

  const data = detailQuery.data;

  return (
    <div className="mx-auto max-w-7xl space-y-5 bg-page px-4 py-6 min-h-full">
      <div>
        <Link to={listTo} className="text-body-sm font-semibold text-brand-deep hover:underline">
          Share of voice
        </Link>
        <h1 className="mt-2 text-headline-md font-semibold text-foreground">
          {data?.keywordDisplay ?? 'Keyword'}
        </h1>
        <p className="mt-1 text-sm text-foreground-muted">
          {data ? marketplaceLabel(data.marketplace) : 'Loading keyword'}
          {data?.snapshotDate ? ` · snapshot ${data.snapshotDate}` : ''}
        </p>
      </div>

      {detailQuery.isLoading ? (
        <div className="flex items-center gap-2 text-foreground-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading keyword
        </div>
      ) : detailQuery.error ? (
        <p className="text-body-sm text-error-ink">{apiErrorMessage(detailQuery.error, 'Failed to load keyword')}</p>
      ) : data && !data.snapshotDate ? (
        <Card className="px-4 py-3 text-body-sm text-foreground-muted">
          No snapshot in this date range yet. The daily sync will store the next result here.
        </Card>
      ) : data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Card className="p-4">
              <p className="text-label-md font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                Search volume
              </p>
              <p className="mt-2 text-headline-md font-semibold">{formatSearchVolume(data.searchVolume30d)}</p>
              <p className="mt-1 text-body-sm text-foreground-muted">30-day exact match</p>
            </Card>
            <Card className="p-4">
              <p className="text-label-md font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                Suggested bid
              </p>
              <p className="mt-2 text-headline-md font-semibold">
                {formatBid(data.suggestedBidMedian, data.currencyCode)}
              </p>
              <p className="mt-1 text-body-sm text-foreground-muted">30-day median</p>
            </Card>
            <Card className="p-4">
              <p className="text-label-md font-semibold uppercase tracking-[0.12em] text-foreground-subtle">Products</p>
              <p className="mt-2 text-headline-md font-semibold">{formatCount(data.productCount)}</p>
              <p className="mt-1 text-body-sm text-foreground-muted">Matching products</p>
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <Bars title="Organic SOV" rows={sovBarRows(data.brands, 'organic')} />
            <Bars title="Sponsored SOV" rows={sovBarRows(data.brands, 'paid')} />
          </div>

          <Card>
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-body-md font-semibold text-foreground">Competitor SOV</h2>
            </div>
            <TableWrap>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Brand</TableHead>
                    <TableHead className="text-right">Organic SOV</TableHead>
                    <TableHead className="text-right">Paid SOV</TableHead>
                    <TableHead className="text-right">Combined SOV</TableHead>
                    <TableHead className="text-right">Organic pos.</TableHead>
                    <TableHead className="text-right">Paid pos.</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {data.brands.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-foreground-muted">
                        This snapshot has no brand rows.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.brands.map((brand) => (
                      <TableRow key={brand.brandName}>
                        <TableCell className={cn(brand.isOwnBrand && 'font-semibold')}>
                          {brand.brandName}
                          {brand.isOwnBrand ? ' · Your brand' : ''}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatSov(brand.organicSov)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatSov(brand.paidSov)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatSov(brand.combinedSov)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatPosition(brand.organicPosition)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatPosition(brand.paidPosition)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrap>
          </Card>
        </>
      ) : null}
    </div>
  );
}
